<Modal
    id="ready-made-content-modal"
    bind:enabled={showAddReadyMadeContentDialog}
    on:close={cancel}
>
    <span slot="title" aria-live="polite">{titleText}</span>
    <div slot="banner">
        <Banner
            visible={isWarningMessageVisible}
            text={warningMessageText}
            type="warning"
            close={i18n.close}
            on:hidden={() => {
                isWarningMessageVisible = false;
            }}
        />
        <Banner
            visible={isErrorMessageVisible}
            text={i18n.errorMessage}
            type="warning"
            close={i18n.close}
            on:hidden={() => {
                isErrorMessageVisible = false;
            }}
        />
        <Banner
            visible={isInfoBannerVisible}
            text={infoMessage}
            closable={false}
        />
    </div>
    <div slot="body" aria-live="polite" area-atomic="true">
        {#if !confirmImport}
            {#if showBreadcrumbs}
                <Breadcrumbs
                    {breadcrumbItems}
                    skipTargetIdentifier="#skip-breadcrumbs-target"
                    dropdownText={i18n.previous}
                    skipText={i18n.skip}
                    currentText={i18n.current}
                    on:breadcrumbSelectedEvent={breadcrumbItemSelected}
                />
            {/if}

            {#if currentListType === listTypes.courseList}
                <TemplateCourseList
                    {i18n}
                    {isSearch}
                    bind:selectedTemplateType
                    {availabilityOfTemplateTypes}
                    {courses}
                    bind:this={templateCourseList}
                    on:searchForCourseTemplates={searchForCourseTemplates}
                    on:templateSelected={onTemplateSelected}
                    on:selectedTemplateTypeChanged={selectedTemplateTypeChanged}
                />
            {:else if currentListType === listTypes.topicList}
                <TopicList {i18n} {topics} on:itemSelected={onTopicSelected} />
            {:else if currentListType === listTypes.planList}
                <PlanList {i18n} {plans} on:itemSelected={onPlanSelected} />
            {:else if currentListType === listTypes.resourceList}
                <ResourceList {i18n} {resources} loadInProgress={loadResourcesInProgress} bind:selectedResources bind:isAllSelected />
            {/if}

            {#if totalCount > 0}
                <Pager
                    {totalCount}
                    {pageIndex}
                    {pageSize}
                    {i18n}
                    on:pageChanged={onPageChanged}
                    on:pageSizeChanged={onPageSizeChanged}
                />
            {/if}
        {:else}
            {#if canSomethingBeImported}
                <div>
                    {#if elementToolsCannotBeCopied?.length > 0}
                        {i18n.resourcesFromTheFollowingToolsCannotBeImported}
                        <ul>
                            {#each elementToolsCannotBeCopied as element}
                                <li>{element.title}</li>
                            {/each}
                        </ul>
                    {/if}
                    {#if elementsWithoutAccess?.length > 0}
                        {i18n.youDontHavePermissionToImportTheFollowingResources}
                        <ul>
                            {#each elementsWithoutAccess as element}
                                <li>{element.title}</li>
                            {/each}
                        </ul>
                    {/if}
                    {#if elementsWithOtherProblems?.length > 0}
                        {i18n.weWereUnableToImportTheFollowingResources}
                        <ul>
                            {#each elementsWithOtherProblems as element}
                                <li>{element.title}</li>
                            {/each}
                        </ul>
                    {/if}
                    {i18n.weWillImportAllOtherResources}
                </div>
            {/if}
        {/if}
    </div>
    <div slot="footer" aria-live="polite" area-atomic="true">
        {#if !confirmImport || canSomethingBeImported}
            <Button
                type="primary"
                htmlType="button"
                large={true}
                loading={isImportInProgress}
                on:click={importSelected}
                text={confirmImport ? i18n.continueImport : i18n.importSelected}
            />
        {/if}
        {#if confirmImport}
            <Button
                type="secondary"
                htmlType="button"
                large={true}
                on:click={backToSelection}
                text={i18n.backToSelection}
            />
        {/if}
        <Button
            type={confirmImport ? "plain" : "secondary"}
            htmlType="button"
            large
            on:click={cancel}
            text={i18n.cancel}
        />
    </div>
</Modal>

<script>
    import Modal from "@itslearning/prometheus/assets/modals/Modal/v2/Modal.svelte";
    import Button from "@itslearning/prometheus/assets/inputs/Button/v1/Button.svelte";
    import Banner from "@itslearning/prometheus/assets/feedback/Banner/v1/Banner.svelte";
    import Breadcrumbs from "@itslearning/prometheus/assets/Navigation/Breadcrumbs/v1/Breadcrumbs.svelte";
    import Pager from "@its-ui/common-components/Pager/Pager.svelte";
    import { formatLanguageString } from '@itslearning/atlas/strings/format';

    import TemplateCourseList from "../TemplateCourseList/TemplateCourseList.svelte";
    import TopicList from "../TopicList/TopicList.svelte";
    import PlanList from "../PlanList/PlanList.svelte";
    import ResourceList from "../ResourceList/ResourceList.svelte";
    import { PAGE_SIZE_S } from "@its-ui/common-components/utils/constants.js";

    import {
        readyMadeContentTemplateTypes,
        courseAvailabilityType,
        listTypes
    } from "../../../constants";
    import {
        searchForCourses,
        searchForPlannerTemplates,
        getTopicsFromCourse,
        getTopicsFromShareablePlanner,
        getPlansInTopicFromCourse,
        getResourcesFromTemplateCourse,
        importResources,
        saveUserPreferencePageSize
    } from "../../../api";

    import { createEventDispatcher } from "svelte";

    export let i18n = {};
    export let availabilityOfTemplateTypes;
    export let showAddReadyMadeContentDialog;
    export let courseId;
    export let organisationId;
    export let plan;
    export let pageSize = PAGE_SIZE_S;
    export let elementCategory;

    let currentListType = listTypes.courseList;

    let selectedTemplateType;

    let breadcrumbItems = [];

    let selectedCourseTemplate;
    let selectedTopic;
    let selectedPlan;

    let searchTitle = "";

    let courses = [];
    let topics = [];
    let plans = [];
    let resources = [];
    let selectedResources = [];

    let pageIndex = 0;
    let totalCount = 0;
    let isImportInProgress = false;
    let loadResourcesInProgress = false;

    let isWarningMessageVisible = false;
    let isErrorMessageVisible = false;

    let isSharedPlan = false;

    let templateCourseList;
    let isAllSelected;
    let confirmImport = false;
    let canSomethingBeImported = false;
    let elementToolsCannotBeCopied;
    let elementsWithoutAccess;
    let elementsWithOtherProblems;

    const dispatch = createEventDispatcher();

    $: if (selectedTemplateType) {
        isSharedPlan =
            selectedTemplateType.value === readyMadeContentTemplateTypes.fromPublishers ||
            selectedTemplateType.value === readyMadeContentTemplateTypes.otherSites;
    }

    $: if (currentListType === listTypes.courseList && selectedTemplateType && (!courses || courses.length === 0)) {
        courses = fetchCourses();
    }

    $: warningMessageText =
        currentListType === listTypes.resourceList
            ? i18n.selectAtLeastOneResourceToImport
            : i18n.navigateToPlanFirstAndChooseAtLeastOneResource;

    $: isSearch = searchTitle && searchTitle.length > 0;

    $: showBreadcrumbs =
        currentListType !== listTypes.courseList && breadcrumbItems && breadcrumbItems.length > 0;

    $: infoMessage = formatLanguageString(i18n.xofYResourcesHaveBeenSelected, selectedResources.length, totalCount);

    $: isInfoBannerVisible = (totalCount > pageSize) && (currentListType === listTypes.resourceList) && (selectedResources.length > 0);

    $: titleText =
        confirmImport
            ? canSomethingBeImported
                ? i18n.weAreUnableToImportSomeResources
                : i18n.weAreUnableToImportTheSelectedResources
            : i18n.importReadyMadeContent;

    function searchForCourseTemplates({ detail }) {
        searchTitle = detail;
        isWarningMessageVisible = false;
        isErrorMessageVisible = false;
        resetCurrentPageIndex();
        courses = fetchCourses();
    }

    function onTemplateSelected({ detail }) {
        selectedCourseTemplate = detail;
        isWarningMessageVisible = false;
        isErrorMessageVisible = false;
        currentListType = listTypes.topicList;
        resetCurrentPageIndex();
        topics = fetchTopics();
    }

    function onTopicSelected({ detail }) {
        selectedTopic = detail;
        isWarningMessageVisible = false;
        isErrorMessageVisible = false;
        currentListType = listTypes.planList;
        resetCurrentPageIndex();
        plans = fetchPlans();
    }

    async function onPlanSelected({ detail }) {
        selectedPlan = detail;
        isWarningMessageVisible = false;
        isErrorMessageVisible = false;
        currentListType = listTypes.resourceList;
        resetCurrentPageIndex();
        resources = await fetchResources();
    }

    async function fetchCourses() {
        const paging = { pageIndex: pageIndex + 1, pageSize };

        try {
            resetSelectedResources();
            let data = [];

            switch (selectedTemplateType.value) {
                case readyMadeContentTemplateTypes.yourCourses:
                case readyMadeContentTemplateTypes.courseTemplates:
                    data = await searchForCourses(
                        {
                            title: searchTitle,
                            sortDescending: false,
                            availabilityType:
                                selectedTemplateType.value ===
                                readyMadeContentTemplateTypes.yourCourses
                                    ? courseAvailabilityType.mineOnly
                                    : courseAvailabilityType.other,
                            organisationId,
                            selectedCourseId: courseId,
                            selectedTopicId: plan && plan.topic ? plan.topic.id : 0,
                            isPush: false,
                            requireTopics: false
                        },
                        paging
                    );

                    break;

                case readyMadeContentTemplateTypes.fromPublishers:
                case readyMadeContentTemplateTypes.otherSites:
                    data = await searchForPlannerTemplates(
                        {
                            title: searchTitle,
                            sortDescending: false,
                            publishersOnly:
                                selectedTemplateType.value ===
                                readyMadeContentTemplateTypes.fromPublishers,
                            organisationId
                        },
                        paging
                    );
                    break;

                default:
                    break;
            }

            const courses = data && data.items && data.items.length > 0 ? data.items : [];

            totalCount = data.virtualCount;

            buildBreadcrumbItems(
                selectedTemplateType.value,
                selectedTemplateType.text,
                currentListType,
                pageIndex,
                totalCount,
                false
            );

            return courses;
        } catch {
            isErrorMessageVisible = true;
        }
    }

    async function fetchTopics() {
        try {
            resetSelectedResources();
            const paging = { pageIndex: pageIndex, pageSize };
            const result = isSharedPlan
                ? await getTopicsFromShareablePlanner(courseId, selectedCourseTemplate.templateId, elementCategory)
                : await getTopicsFromCourse(courseId, selectedCourseTemplate.templateId, paging);

            let topicItems;

            if (isSharedPlan) {
                const startIndex = pageIndex * pageSize;
                const topicsForCurrentPage = result.slice(startIndex, startIndex + pageSize);

                totalCount = result.length;
                topicItems = topicsForCurrentPage;
            } else {
                totalCount = result.total;
                topicItems = result.entityArray;
            }

            buildBreadcrumbItems(
                selectedCourseTemplate.templateId,
                selectedCourseTemplate.name,
                currentListType,
                pageIndex,
                totalCount,
                false
            );

            return topicItems;
        } catch  {
            isErrorMessageVisible = true;
        }
    }

    async function fetchPlans() {
        try {
            let data;

            resetSelectedResources();

            if (isSharedPlan) {
                const startIndex = pageIndex * pageSize;
                const plansForCurrentPage = selectedTopic.plans.entityArray.slice(
                    startIndex,
                    startIndex + pageSize
                );

                totalCount = selectedTopic.plans.total;

                data = plansForCurrentPage;
            } else {
                const paging = { pageIndex: pageIndex, pageSize };
                const result = await getPlansInTopicFromCourse(
                    courseId,
                    selectedCourseTemplate.templateId,
                    selectedTopic.id,
                    paging
                );

                data = result.entityArray;
                totalCount = result.total;
            }

            buildBreadcrumbItems(
                selectedTopic.id,
                selectedTopic.name,
                currentListType,
                pageIndex,
                totalCount,
                false
            );

            return data;
        } catch {
            isErrorMessageVisible = true;
        }
    }

    async function fetchResources() {
        if(!loadResourcesInProgress) {
            loadResourcesInProgress = true;
            isAllSelected = false;

            try {
                let resourceItems;

                if (isSharedPlan && selectedPlan.isLoaded) {
                    const startIndex = pageIndex * pageSize;
                    const resourcesForCurrentPage = selectedPlan.elements.slice(
                        startIndex,
                        startIndex + pageSize
                    );

                    totalCount = selectedPlan.elementsCount;
                    resourceItems = resourcesForCurrentPage;
                } else {
                    const paging = { pageIndex: pageIndex, pageSize };
                    const data = await getResourcesFromTemplateCourse(
                        courseId,
                        selectedCourseTemplate.templateId,
                        selectedTopic.id,
                        selectedPlan.id,
                        paging,
                        elementCategory
                    );

                    totalCount = data.total;
                    resourceItems = data.entityArray;
                }

                buildBreadcrumbItems(
                    selectedPlan.id,
                    selectedPlan.title ? selectedPlan.title : i18n.noTitle,
                    currentListType,
                    pageIndex,
                    totalCount,
                    false
                );

                return resourceItems;
            } catch {
                isErrorMessageVisible = true;
            } finally {
                loadResourcesInProgress = false;
            }
        }
    }

    async function selectedTemplateTypeChanged({ detail }) {
        selectedTemplateType = detail;
        resetCurrentPageIndex();
        await onListReloadHandler();
    }

    async function onPageChanged({ detail }) {
        pageIndex = detail.pageIndex;
        await onListReloadHandler();
    }

    async function onPageSizeChanged({ detail }) {
        resetCurrentPageIndex();
        pageSize = detail.pageSize;

        await saveUserPreferencePageSize(courseId, detail.pageSize);
        await onListReloadHandler();
    }

    async function onListReloadHandler() {
        switch (currentListType) {
            case listTypes.courseList:
                courses = fetchCourses();
                break;
            case listTypes.topicList:
                topics = fetchTopics();
                break;
            case listTypes.planList:
                plans = fetchPlans();
                break;
            case listTypes.resourceList:
                resources = await fetchResources();
                resources.forEach((resource) => {
                    resource.checked = selectedResources.includes(resource.id);
                });
                isAllSelected = resources.length > 0 && resources.every(
                    (resource) => resource.checked
                );
                break;
            default:
                break;
        }
    }

    function breadcrumbItemSelected({ detail }) {
        totalCount = detail.totalCount;
        pageIndex = detail.pageNumber;
        currentListType = detail.type;
        isWarningMessageVisible = false;
        isErrorMessageVisible = false;
        resetSelectedResources();

        const indexOfSelectedBreadcrumbItem = breadcrumbItems.indexOf(detail);

        breadcrumbItems = breadcrumbItems.slice(0, indexOfSelectedBreadcrumbItem + 1);
    }

    async function importSelected() {
        if (selectedResources.length <= 0) {
            isWarningMessageVisible = true;
            scrollToModalTop();

            return;
        }

        if (!isImportInProgress)
        {
            isImportInProgress = true;
            const importResourcesModel = {
                sourceCourseId: selectedCourseTemplate.templateId,
                sourceShareableObjectId: isSharedPlan ? selectedPlan.id : 0,
                sourcePlanId: selectedPlan.id,
                targetPlanId: plan ? plan.id : 0,
                targetTopicId: plan.topic ? plan.topic.id : 0,
                elementIds: selectedResources,
                forceImport: confirmImport
            };

            try {
                const resultModel = await importResources(courseId, importResourcesModel);

                if (!resultModel.isImportCompleted) {
                    confirmImport = true;

                    if (resultModel.elementToolsCannotBeCopied?.length > 0
                        || resultModel.elementsWithoutAccess?.length > 0
                        || resultModel.elementsWithSomeOtherProblems?.length > 0) {
                        canSomethingBeImported = true;
                        elementToolsCannotBeCopied = resultModel.elementToolsCannotBeCopied;
                        elementsWithoutAccess = resultModel.elementsWithoutAccess;
                        elementsWithOtherProblems = resultModel.elementsWithSomeOtherProblems;
                    }
                }
                else {
                    resetModal();
                    dispatch("importCompleted");
                }
            } catch {
                isErrorMessageVisible = true;
            } finally {
                isImportInProgress = false;
            }
        }
    }

    function cancel() {
        resetModal();
    }

    function backToSelection() {
        confirmImport = false;
        canSomethingBeImported = false;
    }

    function resetModal() {
        showAddReadyMadeContentDialog = false;
        isWarningMessageVisible = false;
        isErrorMessageVisible = false;
        confirmImport = false;
        canSomethingBeImported = false;

        resetCurrentPageIndex();
        totalCount = 0;

        searchTitle = "";

        courses = [];
        topics = [];
        plans = [];
        resources = [];

        resetSelectedResources();

        selectedCourseTemplate = null;
        selectedTopic = null;
        selectedPlan = null;
        currentListType = null;

        currentListType = listTypes.courseList;
        setDefaultAvailableSelectedCourseType();
        resetBreadcrumbs();
    }

    function setDefaultAvailableSelectedCourseType() {
        if (templateCourseList) {
            templateCourseList.setDefaultSelectedTemplateType();
        }
    }

    function buildBreadcrumbItems(id, name, type, currentPageNumber, totalCount, resetAllItems) {
        const currentBreadcrumbItem = {
            id: id,
            name: name,
            type: type,
            pageNumber: currentPageNumber,
            totalCount: totalCount
        };

        if (resetAllItems || !breadcrumbItems || breadcrumbItems.length === 0) {
            breadcrumbItems = [currentBreadcrumbItem];
        } else {
            breadcrumbItems[type] = currentBreadcrumbItem;
            breadcrumbItems = [...breadcrumbItems];
        }
    }

    function resetCurrentPageIndex() {
        pageIndex = 0;
    }

    function resetSelectedResources() {
        selectedResources.length = 0;
    }

    function scrollToModalTop() {
        const modalContent = document.querySelector(".prom-modal2__content");

        if (modalContent) {
            modalContent.scrollTop = 0;
        }
    }

    function resetBreadcrumbs() {
        breadcrumbItems = [];
    }
</script>
